import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";
import type {
  ChenHsongMachineDataResponse,
  ChenHsongMachineNodeMapping,
  ChenHsongMachineOverviewField,
  ChenHsongMachineState,
} from "@src/types/factory-dashboard-chen-hsong";

interface SensorValue {
  value: number | string | null;
  time: string;
}

interface MachineSensorEntry {
  meta_data: Record<string, unknown>;
  values: { time: string; value: number | string | null }[];
  timeseries_type: string;
}

const CHEN_HSONG_MACHINE_OVERVIEW_FIELDS: readonly ChenHsongMachineOverviewField[] =
  [
    "machineMode",
    "job",
    "nominalParts",
    "jobGoodPartsCounter",
    "cycleTime",
    "jobBadPartsCounter",
  ];

function normalizeSensorEntry(entry: MachineSensorEntry): SensorValue {
  const latest = entry.values[0];
  return {
    value: latest?.value ?? null,
    time: latest?.time ?? "",
  };
}

const createInitialMachineState = (): ChenHsongMachineState =>
  CHEN_HSONG_MACHINE_OVERVIEW_FIELDS.reduce((acc, field) => {
    acc[field] = { value: null, time: "" };
    return acc;
  }, {} as ChenHsongMachineState);

const getFieldNodePairs = (
  nodes: ChenHsongMachineNodeMapping,
): ReadonlyArray<{ field: ChenHsongMachineOverviewField; nodeId: string }> =>
  CHEN_HSONG_MACHINE_OVERVIEW_FIELDS.reduce<
    Array<{ field: ChenHsongMachineOverviewField; nodeId: string }>
  >((pairs, field) => {
    const nodeId = nodes[field];
    if (nodeId) {
      pairs.push({ field, nodeId });
    }
    return pairs;
  }, []);

function buildMachineState(
  entries: MachineSensorEntry[],
  fields: readonly ChenHsongMachineOverviewField[],
): ChenHsongMachineState {
  return fields.reduce((acc, field, index) => {
    const entry = entries[index];
    if (entry) {
      acc[field] = normalizeSensorEntry(entry);
    }
    return acc;
  }, createInitialMachineState());
}

export function hasChenHsongMachineData(
  state: ChenHsongMachineState | undefined,
): boolean {
  return !!state && Object.values(state).some((v) => v.value != null);
}

export const fetchChenHsongMachineData = async (
  group: string,
  nodes: ChenHsongMachineNodeMapping,
): Promise<ChenHsongMachineDataResponse> => {
  const fieldNodePairs = getFieldNodePairs(nodes);
  const now = new Date();
  const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

  const queryParams = {
    limit: 1,
    from: thirtyMinutesAgo.toISOString(),
    to: now.toISOString(),
    limit_order: "DESC" as const,
  };

  try {
    const sensorResults = await Promise.all(
      fieldNodePairs.map(({ nodeId }) =>
        getQueryTimeseriesForNodeUseCase({
          group,
          source: nodeId,
          ...queryParams,
        }),
      ),
    );

    if (sensorResults.some((res) => !res.success)) {
      console.error(
        `One or more ChenHsong queries failed for machine ${group}:`,
        sensorResults,
      );
      return {};
    }

    const entries = sensorResults.map((res) => res.data as MachineSensorEntry);

    return {
      [group]: buildMachineState(
        entries,
        fieldNodePairs.map((pair) => pair.field),
      ),
    };
  } catch (err) {
    console.error(`Failed to fetch ChenHsong data for machine ${group}`, err);
    return {};
  }
};
